import { Helmet } from "react-helmet-async"
import { useParams, Link, useNavigate } from "react-router-dom"
import { useState, useEffect } from "react"
import { ArrowLeft, Mail, Phone, MapPin, Briefcase, Clock, AlertCircle } from "lucide-react"
import { Button } from "../../components/ui/button"

const WP_API_BASE = "/wp-json/wp/v2"

export default function CareerDetail() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [jobData, setJobData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchJobDetail = async () => {
      try {
        const res = await fetch(`${WP_API_BASE}/careers?slug=${slug}&acf_format=standard&_embed`)
        const data = await res.json()

        if (!data || data.length === 0) {
          setJobData(null)
          return
        }

        const job = data[0]
        setJobData({
          title: job.title.rendered,
          description: job.acf?.short_description || job.content.rendered,
          category: job.acf?.job_category || "General",
          type: job.acf?.job_type || "Full Time",
          location: job.acf?.job_location || "Hyderabad",
          email: job.acf?.application_email || "resume@pranaamhospitals.in",
          whatsapp: job.acf?.whatsapp_number || "90259915532",
          isExpired: job.acf?.is_expired || false
        })
      } catch (err) {
        console.error("Error fetching job details:", err)
      } finally {
        setLoading(false)
      }
    }
    fetchJobDetail()
  }, [slug])

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#A32725]"></div>
    </div>
  )

  if (!jobData) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4 text-center">
      <h1 className="text-3xl font-bold mb-4 text-[#A32725]">Job Not Found</h1>
      <p className="text-gray-600 mb-8 text-lg">The career opportunity you are looking for does not exist or has been removed.</p>
      <Button asChild className="bg-black hover:bg-gray-800">
        <Link to="/careers">Back to Careers</Link>
      </Button>
    </div>
  )

  return (
    <>
      <Helmet>
        <title>{jobData.title} Details - Pranaam Hospitals</title>
      </Helmet>

      <main className="min-h-screen bg-gray-50 pb-12">
        {/* Header Section */}
        <div className="bg-black text-white py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <Link to="/careers" className="inline-flex items-center text-gray-400 hover:text-white mb-6 transition-colors">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Careers
              </Link>
              <h1 className="text-3xl md:text-5xl font-bold mb-4 uppercase tracking-wide">{jobData.title}</h1>

              <div className="flex flex-wrap gap-6 text-sm md:text-base opacity-90">
                <div className="flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-[#A32725]" />
                  {jobData.category}
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#A32725]" />
                  {jobData.type}
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#A32725]" />
                  {jobData.location}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 -mt-8">
          <div className="max-w-4xl mx-auto">
            {/* Expired Job Alert */}
            {jobData.isExpired && (
              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-6 flex items-center gap-3 shadow-sm rounded-r-lg">
                <AlertCircle className="w-6 h-6 text-amber-500" />
                <p className="text-amber-800 font-bold">Sorry! This job has expired.</p>
              </div>
            )}

            <div className="grid lg:grid-cols-5 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-3 space-y-8">
                <div className="bg-white rounded-xl shadow-lg p-6 md:p-10">
                  <h2 className="text-2xl font-bold text-black mb-6 border-b pb-4">Job Description</h2>
                  <div
                    className="prose prose-lg max-w-none text-gray-700 mb-6 font-medium job-detail-content"
                    dangerouslySetInnerHTML={{ __html: `${jobData.title} : ${jobData.description}` }}
                  />

                  <div className="mt-8 pt-6 border-t border-gray-100 space-y-4">
                    <p className="text-black text-lg">
                      <span className="font-bold">Job Category:</span> {jobData.category}
                    </p>
                    <p className="text-black text-lg">
                      <span className="font-bold">Job Type:</span> {jobData.type}
                    </p>
                    <p className="text-black text-lg">
                      <span className="font-bold">Job Location:</span> {jobData.location}
                    </p>
                  </div>
                </div>
              </div>

              {/* Sidebar / Contact Details */}
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-white rounded-xl shadow-lg p-6 border-t-4 border-[#A32725]">
                  <h3 className="text-xl font-bold text-black mb-6">How to Apply</h3>
                  <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                    Please share your Resumes to the given Contact details :
                  </p>

                  <div className="space-y-4">
                    <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg group hover:bg-gray-100 transition-colors">
                      <div className="w-10 h-10 bg-[#A32725]/10 rounded-full flex-shrink-0 flex items-center justify-center text-[#A32725]">
                        <Mail className="w-5 h-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-gray-500 uppercase font-bold tracking-tighter">Email :</p>
                        <a href={`mailto:${jobData.email}`} className="text-sm font-bold text-black hover:text-[#A32725] transition-colors whitespace-nowrap">
                          {jobData.email}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg group hover:bg-gray-100 transition-colors">
                      <div className="w-10 h-10 bg-green-50 rounded-full flex items-center justify-center text-green-600">
                        <Phone className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase font-bold tracking-tighter">Whatsapp :</p>
                        <a href={`https://wa.me/${jobData.whatsapp}`} target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-black hover:text-green-600 transition-colors">+{jobData.whatsapp}</a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  )
}